import React, { useEffect, useMemo, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../ui'
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard'
import { getSeccionTemplates } from '../../lib/supabase'
import { SECCION_TIPOS, getSeccionTipoLabel, getSeccionTipoColor } from '../../constants/seccionTipos'

const formatDuracion = (totalSeg) => {
  const min = Math.floor(totalSeg / 60)
  const seg = totalSeg % 60
  return `${min}:${seg.toString().padStart(2, '0')}`
}

const ImportarPlantillasModal = ({ open, onOpenChange, onImport, deportes = [] }) => {
  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) })
  const [plantillas, setPlantillas] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [filtroTipo, setFiltroTipo] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [seleccionadas, setSeleccionadas] = useState([])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      const tipo = filtroTipo === 'todos' ? null : filtroTipo
      const { data, error: err } = await getSeccionTemplates({ tipo })
      if (cancelled) return
      if (err) setError('No pudimos cargar las plantillas')
      else setPlantillas(data || [])
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [open, filtroTipo])

  useEffect(() => {
    if (open) {
      setSeleccionadas([])
      setBusqueda('')
      setFiltroTipo('todos')
      guard.resetDirty()
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtradas = useMemo(() => {
    let base = plantillas
    // Solo plantillas del/los deporte(s) del programa + las Generales ('todos').
    if (deportes && deportes.length > 0) {
      base = base.filter((p) => (p.deporte || 'todos') === 'todos' || deportes.includes(p.deporte))
    }
    if (!busqueda.trim()) return base
    const q = busqueda.toLowerCase()
    return base.filter((p) =>
      p.nombre.toLowerCase().includes(q) ||
      (p.descripcion || '').toLowerCase().includes(q)
    )
  }, [plantillas, busqueda, deportes])

  const toggleSel = (id) => {
    guard.markDirty()
    setSeleccionadas((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  const handleImportar = () => {
    const elegidas = plantillas.filter((p) => seleccionadas.includes(p.id))
    const secciones = elegidas.map((p) => ({
      nombre: p.nombre,
      duracion_seg: p.duracion_seg,
      descripcion: p.descripcion || '',
      tipo: p.tipo,
      habilidad_ids: p.habilidad_ids || []
    }))
    onImport(secciones)
    guard.resetDirty()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-2xl overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Importar plantillas de seccion</DialogTitle>
          <DialogDescription>
            Selecciona una o varias plantillas. Se copiaran al final del programa actual y podras editarlas despues.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filtros */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Label>Buscar</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Nombre o descripción..."
                className="pl-8"
              />
            </div>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="mt-1 w-full sm:w-48">
                <SelectValue placeholder="Todos los tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tipos</SelectItem>
                {SECCION_TIPOS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Listado */}
        <div className="flex-1 overflow-y-auto min-h-0 -mx-6 px-6">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Cargando plantillas...
            </div>
          ) : filtradas.length === 0 ? (
            <div className="rounded-md border border-dashed border-input px-4 py-10 text-center text-sm text-muted-foreground">
              {plantillas.length === 0
                ? 'No hay plantillas creadas todavia. Ve a "Plantillas de seccion" en el sidebar para crear la primera.'
                : 'Ninguna plantilla coincide con los filtros.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filtradas.map((p) => {
                const isSelected = seleccionadas.includes(p.id)
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleSel(p.id)}
                    className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                      isSelected
                        ? 'border-primary bg-accent'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSel(p.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="text-sm font-medium text-foreground truncate">
                          {p.nombre}
                        </span>
                        <Badge variant="outline" className={`text-[10px] ${getSeccionTipoColor(p.tipo)}`}>
                          {getSeccionTipoLabel(p.tipo)}
                        </Badge>
                      </div>
                      <span className="text-sm font-mono text-foreground shrink-0">
                        {formatDuracion(p.duracion_seg)}
                      </span>
                    </div>
                    {p.descripcion && (
                      <p className="text-xs text-muted-foreground mt-1 ml-6 line-clamp-2">{p.descripcion}</p>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-3">
          <div className="text-xs text-muted-foreground mr-auto">
            {seleccionadas.length > 0 && (
              <>{seleccionadas.length} seleccionada{seleccionadas.length === 1 ? '' : 's'}</>
            )}
          </div>
          <Button variant="outline" onClick={() => guard.onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleImportar} disabled={seleccionadas.length === 0}>
            Importar {seleccionadas.length > 0 && `(${seleccionadas.length})`}
          </Button>
        </DialogFooter>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  )
}

export default ImportarPlantillasModal
